<script lang="ts">
    import { inject, type Ref, type InjectionKey } from 'vue'

    let _id = 0

    type APIItem = {
        id: number
        title: string
        url: string
    }

    type APIReponse = {
        items: APIItem[]
        errorOccurred: boolean
        errorMessage: string | null
    }

    type SiteSearchRootContext = {
        term: Ref<string>
        expanded: Ref<boolean>
        id: number
        input: Ref<HTMLInputElement | undefined>
    }

    const key = Symbol() as InjectionKey<SiteSearchRootContext>
    export const injectSiteSearchRootContext = () => inject(key)
</script>
<script setup lang="ts">
    import { ref, computed, provide, watch } from 'vue'
    import { refDebounced, useFetch, useFocusWithin, useEventListener, whenever } from '@vueuse/core'

    const el = ref<HTMLDivElement>()
    const id = _id++

    const props = defineProps<{
        noResultsMessage: string
        resultsClass: string
        disabled: boolean
    }>()

    const term = ref('')
    const termDebounced = refDebounced(term, 300)
    const url = computed(() => `/api/search/getaclist?term=${termDebounced.value}`)
    const { data, error, execute } = useFetch(url, { immediate: false, refetch: false }).get().json<APIReponse>()
    watch(termDebounced, (newTerm) => newTerm.length >= 3 ? execute() : (data.value = null))
    watch(term, (newTerm) => newTerm.length < 3 && (data.value = null)) // clear results if no term
    
    const input = ref<HTMLInputElement>()
    const { focused } = useFocusWithin(el)
    const expanded = computed(() => Boolean(
        !props.disabled && focused.value && ( // not disabled & have focus
            data.value?.items?.length // have items
            || (!error.value && data.value && !data.value?.errorOccurred && props.noResultsMessage) // or no results and we have a noResultsMessage
            || (data.value?.errorMessage) // or an error occurred
        )
    ))
    provide(key, {
        term,
        input,
        expanded,
        id
    })

    // Accessibility shortcuts
    const selected = ref<number>()
    whenever(data, () => selected.value = undefined) // reset when new results come in

    useEventListener(input, 'keydown', (event) => {
        const len = data.value?.items.length
        if (!len) return
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            selected.value = selected.value == null ? 0 : Math.max(selected.value + 1, len)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            selected.value = !selected.value ? undefined : selected.value - 1
        } else if (event.key === 'Enter' && data.value && selected.value != null) {
            event.preventDefault()
            window.location.href = data.value.items[selected.value].url
        }
    })
</script>
<template>
    <div ref="el" class="relative" :class="{ '!rounded-b-none': expanded }">
        <slot :expanded />
        <div v-if="expanded" :class="[resultsClass, 'absolute top-full overflow-hidden bg-light rounded-b-md border-gray-400 border-b border [border-top-style:dashed]']" @mouseleave="selected = undefined">
            <ol v-if="data?.items?.length" :id="`site-search-results-${id}`">
                <li :key="item.id" :aria-selected="i === selected" v-for="(item, i) in data?.items" class="text-sm text-dusk aria-selected:bg-twilight aria-selected:text-light" @mouseenter="selected = i">
                    <a tabindex="-1" :href="item.url" class="block py-2.5 px-4">{{ item.title }}</a>
                </li>
            </ol>
            <p class="py-2.5 px-4" v-else-if="!error && data && !data?.errorOccurred && props.noResultsMessage">
                {{ props.noResultsMessage }}
            </p>
            <p class="py-2.5 px-4" v-else>
                {{ data?.errorMessage }}
            </p>
        </div>
    </div>
</template>